import { Pipe, PipeTransform } from '@angular/core';

/**
 * Convierte una URL de imagen LOCAL (.webp / .jpg / .jpeg / .png) en su .avif.
 *
 * Devuelve null para URLs externas (Bokun, Cloudinary, http(s)://...) porque
 * no podemos generar AVIFs para imágenes que no son nuestras. Cuando se usa
 * con `[srcset]` en un <source>, Angular omite el atributo si el valor es null,
 * efectivamente removiendo el <source> AVIF para esas URLs externas. El browser
 * cae directo al <img> con la imagen original.
 *
 * Uso típico:
 *   <picture>
 *     <source [srcset]="img | toAvif" type="image/avif" />
 *     <img [src]="img" ... />
 *   </picture>
 *
 * Lógica:
 *   'assets/imgs/blog-cards/92.webp'       → 'assets/imgs/blog-cards/92.avif'  (local)
 *   '/assets/imgs/hero.webp'               → '/assets/imgs/hero.avif'          (local absoluto)
 *   'https://cdn.bokun.com/foo.jpg'        → null                              (externo, no convertir)
 *   'https://res.cloudinary.com/foo.png'   → null                              (externo, no convertir)
 *   'data:image/png;base64,...'            → null                              (data URI)
 */
@Pipe({
    name: 'toAvif',
})
export class ToAvifPipe implements PipeTransform {
    private static readonly EXT_RE = /\.(webp|jpe?g|png)(?:\?.*)?$/i;
    private static readonly EXTERNAL_RE = /^(https?:)?\/\//i;
    private static readonly DATA_URI_RE = /^data:/i;

    transform(value: string | null | undefined): string | null {
        if (!value || typeof value !== 'string') return null;

        // Skip external URLs (Bokun, Cloudinary, S3, etc.) — we don't host those AVIFs.
        if (ToAvifPipe.EXTERNAL_RE.test(value)) return null;

        // Skip data URIs
        if (ToAvifPipe.DATA_URI_RE.test(value)) return null;

        // Skip if already AVIF
        if (value.endsWith('.avif')) return value;

        // Skip if no matching extension (defensive)
        if (!ToAvifPipe.EXT_RE.test(value)) return null;

        return value.replace(ToAvifPipe.EXT_RE, '.avif');
    }
}
